import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Star, ExternalLink, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { COMPANY, SERVICE_AREAS } from "../data/site";

/* eslint-disable react/no-unescaped-entities */

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-stone-900 text-stone-200">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4">
              <img src={COMPANY.logoWhiteSmall} alt="TSB" width="240" height="128" loading="lazy" className="h-14 md:h-16 w-auto" />
              <div>
                <div className="font-display font-black text-white text-lg tracking-tight">
                  TEXAN STRONG BUILDS
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-red-500 mt-1">
                  Austin TX · Since 2014
                </div>
              </div>
            </div>
            <p className="mt-6 text-stone-300 leading-relaxed text-sm max-w-sm">
              Austin's trusted concrete &amp; landscape contractor. Built strong. Built local.
              Built for Texas.
            </p>
            <a
              href={COMPANY.gmb}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-gmb-link"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-red-500"
            >
              <Star size={16} fill="#facc15" stroke="#facc15" />
              See us on Google Business
              <ExternalLink size={14} />
            </a>

            <div className="mt-5 flex items-center gap-3" data-testid="footer-social-links">
              <a
                href={COMPANY.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Texan Strong Builds on Facebook"
                data-testid="footer-facebook-link"
                className="w-10 h-10 flex items-center justify-center bg-stone-800 hover:bg-red-600 transition-colors text-white"
              >
                <Facebook size={18} />
              </a>
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Texan Strong Builds on Instagram"
                data-testid="footer-instagram-link"
                className="w-10 h-10 flex items-center justify-center bg-stone-800 hover:bg-red-600 transition-colors text-white"
              >
                <Instagram size={18} />
              </a>
              <a
                href={COMPANY.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Texan Strong Builds on LinkedIn"
                data-testid="footer-linkedin-link"
                className="w-10 h-10 flex items-center justify-center bg-stone-800 hover:bg-red-600 transition-colors text-white"
              >
                <Linkedin size={18} />
              </a>
              <a
                href={COMPANY.youtube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Texan Strong Builds on YouTube"
                data-testid="footer-youtube-link"
                className="w-10 h-10 flex items-center justify-center bg-stone-800 hover:bg-red-600 transition-colors text-white"
              >
                <Youtube size={18} />
              </a>
            </div>
          </div>

          <nav className="md:col-span-2" aria-label="Concrete services">
            <div className="text-xs uppercase tracking-[0.2em] text-red-500 font-bold mb-4">
              Concrete
            </div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className="hover:text-white text-stone-300">Driveways</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Patios & Slabs</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Stamped Concrete</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Foundations</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Retaining Walls</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Concrete Repair</Link></li>
            </ul>
          </nav>

          <nav className="md:col-span-2" aria-label="Landscape services">
            <div className="text-xs uppercase tracking-[0.2em] text-red-500 font-bold mb-4">
              Landscape
            </div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/xeriscape" className="hover:text-white text-stone-300">Xeriscape</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Irrigation</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Outdoor Lighting</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Artificial Turf</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Fire Pits</Link></li>
              <li><Link to="/services" className="hover:text-white text-stone-300">Pavers</Link></li>
            </ul>
          </nav>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-red-500 font-bold mb-4">
              Contact
            </div>
            <address className="not-italic">
            <ul className="space-y-3 text-sm">
              <li>
                <a href={COMPANY.phoneTel} data-testid="footer-call-link" className="flex items-center gap-3 hover:text-white text-stone-300">
                  <Phone size={16} /> {COMPANY.phone}
                </a>
              </li>
              <li>
                <a href={COMPANY.emailMailto} data-testid="footer-email-link" className="flex items-center gap-3 hover:text-white text-stone-300">
                  <Mail size={16} /> {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-stone-300" data-testid="footer-address">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span>{COMPANY.fullAddress} — serving the entire Austin metro &amp; Hill Country.</span>
              </li>
              <li className="text-stone-400 text-xs pt-2 border-t border-stone-800 mt-3">
                {COMPANY.hours}
              </li>
            </ul>
            </address>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-stone-800">
          <div data-testid="footer-service-areas-title" className="text-xs uppercase tracking-[0.2em] text-stone-400 font-bold mb-4">
            Service Areas
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-stone-300">
            {SERVICE_AREAS.map((a) => (
              <span key={a}>{a}</span>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-stone-800 flex flex-col md:flex-row justify-between gap-4 text-xs text-stone-400">
          <div data-testid="footer-copyright">© {new Date().getFullYear()} Texan Strong Builds. Bonded & insured in Texas.</div>
          <div className="flex gap-6">
            <Link to="/blog" data-testid="footer-bottom-blog-link" className="hover:text-white">Blog</Link>
            <Link to="/contact" data-testid="footer-bottom-contact-link" className="hover:text-white">Free Estimate</Link>
            <a href={COMPANY.gmb} data-testid="footer-bottom-gmb-link" target="_blank" rel="noopener noreferrer" className="hover:text-white">Google Business</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
